<template>
  <div
    ref="root"
    class="rd rd--finance fin"
    :class="{ 'fin--phone': phone, 'ui-compact': phone, 'fin--print-panel': printingPanel }"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('finance_page_title')" @select="openScreen">
        <template #tools>
          <button v-if="!phone" type="button" class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain" :title="`${$t('report_desktop.shortcuts')} (?)`" :aria-label="$t('report_desktop.shortcuts')" :aria-expanded="String(helpOpen)" @click="helpOpen = !helpOpen">
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
        </template>
      </report-nav>

      <finance-period-bar v-if="showPeriod" ref="bar" :from="period.from" :to="period.to" :presets="presets" :compact="phone || layout === 'sm'" @period="setPeriod" />

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ $t("finance_page_title") }}</h1>
            <p class="rd-head__sub">{{ restaurantName }}</p>
          </div>
          <div class="rd-head__right fin-head-tools">
            <!-- telefon: alati ekrana (lupa, izvoz) su u istom redu kao osvježi i štampa; ekran javlja koje nudi (setScreenTools) -->
            <template v-if="phone && screenTools">
              <ui-icon-button
                v-if="screenTools.search"
                class="fin-search-toggle"
                :class="{ 'is-on': screenTools.search.on }"
                icon="mdi-magnify"
                size="sm"
                :label="$t('search')"
                :aria-expanded="String(screenTools.search.on)"
                @click="screenTools.search.toggle"
              />
              <finance-export v-if="screenTools.export" compact :filename="screenTools.export.filename" :get-data="screenTools.export.getData" :disabled="screenTools.export.disabled" @print="printPage" />
            </template>
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="`${$t('refresh')} (r)`" :aria-label="$t('refresh')" @click="reload">
              <v-icon size="18">mdi-refresh</v-icon>
            </button>
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="$t('print')" :aria-label="$t('print')" @click="printPage">
              <v-icon size="18">mdi-printer-outline</v-icon>
            </button>
          </div>
        </div>

        <component :is="screen" ref="screen" :key="screenKey" :period="period" @navigate="openScreen" />
      </div>
    </div>

    <report-detail-panel :stack="panelStack" @close="closePanel" @back="panelBack" @open="openPanel" @toast="onPanelToast" />
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false" />
    <ui-toast :toast="toast" @action="runToastAction" />
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "./financeShell.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import restaurantFinanceService from "@/services/restaurantFinance";
import FinancePeriodBar from "./parts/FinancePeriodBar.vue";
import FinanceExport from "./parts/FinanceExport.vue";
import FinanceEarnings from "./screens/FinanceEarnings.vue";
import FinanceSettlements from "./screens/FinanceSettlements.vue";
import FinanceTransactions from "./screens/FinanceTransactions.vue";
import FinanceContract from "./screens/FinanceContract.vue";
import { DEFAULT_FINANCE_TAB, DEFAULT_TX_VIEW, FINANCE_QUERY, FINANCE_TABS, TX_VIEWS, buildFinancePresets, parseRange } from "./financeQuery";

const TAB_ICONS = { earnings: "mdi-cash-multiple", settlements: "mdi-file-document-check-outline", transactions: "mdi-swap-horizontal", contract: "mdi-handshake-outline" };
const SCREENS = { earnings: "finance-earnings", settlements: "finance-settlements", transactions: "finance-transactions", contract: "finance-contract" };

/**
 * Finansije (/restaurant/finance): gornja navigacija (Zarada, Obračuni, Transakcije, Ugovor i cijene), zajednička traka
 * perioda (Zarada i Transakcije), ekran i detalji u desnom panelu istog kao u izvještajima. Isti raspored važi i na
 * telefonu (širina do 600 px): traka perioda postaje jedno dugme, tabele kartice, panel preko cijelog ekrana.
 * Stanje u adresi: ?t=tab&from=&to=&v=prikaz transakcija.
 */
export default {
  name: "FinanceShell",
  mixins: [hasReportShell],
  components: { ReportNav, ReportHelpPopover, ReportDetailPanel, FinancePeriodBar, FinanceExport, FinanceEarnings, FinanceSettlements, FinanceTransactions, FinanceContract },
  data: () => ({
    // broj zahtjeva za refundaciju na čekanju (značka na tabu Transakcije); puni ga ekran Transakcije
    pendingRefunds: 0,
    printingPanel: false,
    // alati koje aktivni ekran nudi u zaglavlju na telefonu: { owner, search: { on, toggle }, export: { filename, getData, disabled } }
    screenTools: null,
  }),
  computed: {
    phone() {
      return Boolean(this.device && this.device.isMobile);
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
    restaurantId() {
      return this.restaurant ? this.restaurant.id : null;
    },
    restaurantName() {
      return this.toLatin((this.restaurant && this.restaurant.name) || "");
    },
    currency() {
      return (this.restaurant && this.restaurant.settings && this.restaurant.settings.price) || "KM";
    },
    isAdmin() {
      return Boolean(this.$store.state.user && this.$store.state.user.user && this.$store.state.user.user.type === "admin");
    },
    presets() {
      return buildFinancePresets();
    },
    menuKeys() {
      return FINANCE_TABS;
    },
    currentKey() {
      const wanted = this.$route.query[FINANCE_QUERY.tab];
      return FINANCE_TABS.includes(wanted) ? wanted : DEFAULT_FINANCE_TAB;
    },
    txView() {
      const wanted = this.$route.query[FINANCE_QUERY.view];
      return TX_VIEWS.includes(wanted) ? wanted : DEFAULT_TX_VIEW;
    },
    period() {
      const range = parseRange(this.$route.query[FINANCE_QUERY.from], this.$route.query[FINANCE_QUERY.to]);
      if (range) {
        return { from: range[0], to: range[1] };
      }
      const month = this.presets[0];
      return { from: month.from, to: month.to };
    },
    showPeriod() {
      return this.currentKey === "earnings" || (this.currentKey === "transactions" && this.txView !== "refunds");
    },
    screen() {
      return SCREENS[this.currentKey];
    },
    screenKey() {
      return `${this.currentKey}-${this.restaurantId}-${this.reloadKey}`;
    },
    menuGroups() {
      const items = FINANCE_TABS.map((key) => ({
        key,
        icon: TAB_ICONS[key],
        title: this.$t(`finance_tab_${key}`),
        badge: key === "transactions" && this.pendingRefunds ? this.pendingRefunds : "",
      }));
      return [{ key: "finance", title: this.$t("finance_shell.group"), items }];
    },
    shortcuts() {
      return [
        { keys: "r", label: this.$t("finance_shell.keys.refresh") },
        { keys: "1  2  3  4", label: this.$t("finance_shell.keys.screens") },
        { keys: "/", label: this.$t("finance_shell.keys.search") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  watch: {
    currentKey() {
      this.closePanel();
      window.scrollTo(0, 0);
    },
    restaurantId() {
      this.closePanel();
    },
  },
  created() {
    this.loadPendingRefunds();
  },
  methods: {
    // hasReportShell poziva ove tri kad korisnik pritisne r, [ ili ] i broj
    reload() {
      this.reloadKey += 1;
      this.loadPendingRefunds();
    },
    shiftDay() {
      // finansije nemaju pomjeranje po danu
    },
    openScreen(key) {
      this.setQuery({ [FINANCE_QUERY.tab]: key === DEFAULT_FINANCE_TAB ? undefined : key });
    },
    setPeriod({ from, to }) {
      // podrazumijevani period (ovaj mjesec) se ne upisuje u adresu
      const isDefault = from === this.presets[0].from && to === this.presets[0].to;
      this.setQuery({ [FINANCE_QUERY.from]: isDefault ? undefined : from, [FINANCE_QUERY.to]: isDefault ? undefined : to });
    },
    setTxView(view) {
      this.setQuery({ [FINANCE_QUERY.view]: view === DEFAULT_TX_VIEW ? undefined : view });
    },
    /** Ekran javlja alate za zaglavlje (null = nema). `owner` je ekran: stari ekran pri uništenju ne smije obrisati alate novog. */
    setScreenTools(owner, tools) {
      this.screenTools = tools ? Object.freeze({ ...tools, owner }) : null;
    },
    clearScreenTools(owner) {
      if (this.screenTools && this.screenTools.owner === owner) this.screenTools = null;
    },
    setQuery(patch) {
      const query = { ...this.$route.query, ...patch };
      Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);
      this.$router.replace({ query }).catch(() => {});
    },
    /** Štampa tabele aktivnog ekrana (kao izvještaji); ekran bez tabele (ugovor) štampa stranicu. */
    printPage() {
      const screen = this.$refs.screen;
      if (screen && typeof screen.printTable === "function") {
        screen.printTable();
      } else {
        window.print();
      }
    },
    /** Štampa samo panel (obračun): ostatak stranice se u štampi sakriva klasom na korenu. */
    printPanel() {
      this.printingPanel = true;
      this.$nextTick(() => {
        window.print();
        setTimeout(() => {
          this.printingPanel = false;
        }, 400);
      });
    },
    onPanelToast(text, options) {
      this.showToast(text, options || {});
    },
    async loadPendingRefunds() {
      if (!this.restaurantId) {
        return;
      }
      try {
        const { data } = await restaurantFinanceService.getRefunds(this.restaurantId);
        this.pendingRefunds = ((data && data.data) || []).filter((refund) => refund.status === "pending").length;
      } catch (error) {
        this.pendingRefunds = 0;
      }
    },
    onKeydown(event) {
      if (event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const target = event.target;
        const typing = target && (["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable);
        const search = document.querySelector(".ui-search input");
        if (!typing && search && !document.querySelector(".v-dialog--active")) {
          event.preventDefault();
          search.focus();
        }
        return;
      }
      hasReportShell.methods.onKeydown.call(this, event);
    },
  },
};
</script>
